(() => {
  if (window.rudcaProductDiscoveryReady) return;
  window.rudcaProductDiscoveryReady = true;
  const instances = new WeakMap();
  const productKey = (value) => {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin ? url.pathname.match(/\/products\/([^/]+)/)?.[1] || '' : '';
    } catch (_) {
      return '';
    }
  };
  const cardKey = (card) => productKey(card.dataset.discoveryProductUrl || card.querySelector('.product-item__title')?.getAttribute('href'));
  const cursorKey = (url) => {
    const key = new URL(url);
    key.hash = '';
    key.searchParams.sort();
    return key.href;
  };
  const initialize = (root) => {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const cleanups = [];
    const shelves = Array.from(root.querySelectorAll('[data-discovery-shelf]'));
    const batchSize = Math.min(8, Math.max(4, Number(root.dataset.discoveryLimit) || 6));
    const states = new Map();
    const currentKey = productKey(root.dataset.currentProductUrl);
    const seen = new Set([currentKey]);
    root.querySelectorAll('.rudca-discovery__item').forEach((item) => seen.add(cardKey(item)));
    const publishProducts = () => {
      const keys = Array.from(new Set(Array.from(root.querySelectorAll('.rudca-discovery__item')).map(cardKey).filter(Boolean)));
      root.dataset.discoveryProductKeys = JSON.stringify(keys);
      root.dispatchEvent(new CustomEvent('rudca:discovery:updated', { bubbles: true, detail: { productKeys: keys, currentProductKey: currentKey } }));
    };
    const sourceURL = (shelf) => {
      if (!shelf.dataset.discoverySource || shelf.dataset.discoveryShelf === 'pairings') return null;
      try {
        const url = new URL(shelf.dataset.discoverySource, window.location.origin);
        if (url.origin !== window.location.origin || !/\/collections\/[^/]+\/?$/.test(url.pathname)) return null;
        url.searchParams.set('filter.v.availability', '1');
        const pageURL = new URL(window.location.href);
        for (const name of ['preview_theme_id', 'preview_key', '_fd', 'pb', 'locale']) {
          if (pageURL.searchParams.has(name)) url.searchParams.set(name, pageURL.searchParams.get(name));
        }
        url.hash = '';
        return url;
      } catch (_) {
        return null;
      }
    };
    // Follow only links returned by this exact native collection. Keep its filters,
    // locale path and preview context when Shopify omits them from a pagination link.
    const pageURL = (value, state, base) => {
      const url = new URL(value, base);
      if (url.origin !== state.source.origin || url.pathname !== state.source.pathname) throw new Error('Invalid collection cursor');
      for (const [name, value] of state.source.searchParams) {
        if (name === 'page' || name === 'cursor' || name === 'page_info') continue;
        if (url.searchParams.has(name) && url.searchParams.get(name) !== value) throw new Error('Changed collection filter');
        if (!url.searchParams.has(name)) url.searchParams.append(name, value);
      }
      url.hash = '';
      return url;
    };
    const hasMore = (state) => state.buffer.length > 0 || state.cursor !== null;
    const nearEnd = (state) => state.list.scrollWidth - state.list.clientWidth - Math.abs(state.list.scrollLeft) <= Math.max(180, state.list.clientWidth * .65);
    const updateShelf = (state) => {
      const { shelf, list } = state;
      const hasCards = list.children.length > 0;
      shelf.hidden = state.checked && !hasCards && !hasMore(state) && !state.error;
      shelf.querySelector('.rudca-discovery__rail').hidden = !hasCards;
      const browse = shelf.querySelector('[data-discovery-browse]');
      if (browse) browse.hidden = hasCards || state.loading || state.error || hasMore(state);
      const overflow = list.scrollWidth > list.clientWidth + 2;
      shelf.querySelector('[data-discovery-navigation]').hidden = !hasCards || (!overflow && !hasMore(state));
      shelf.querySelector('[data-discovery-previous]').disabled = Math.abs(list.scrollLeft) <= 2;
      shelf.querySelector('[data-discovery-next]').disabled = state.loading || (!hasMore(state) && Math.abs(list.scrollLeft) >= list.scrollWidth - list.clientWidth - 2);
      const feedback = shelf.querySelector('[data-discovery-feedback]');
      if (feedback) feedback.hidden = !state.error;
      const errorMessage = shelf.querySelector('[data-discovery-error-message]');
      if (errorMessage) errorMessage.textContent = state.error ? shelf.dataset.discoveryError || 'Couldn’t load more products.' : '';
      const retry = shelf.querySelector('[data-discovery-retry]');
      if (retry) retry.disabled = state.loading;
      const more = shelf.querySelector('[data-discovery-more]');
      if (more) {
        more.hidden = !hasMore(state) || state.error || state.loading || (!state.paused && hasCards);
        more.disabled = state.loading;
      }
      root.hidden = shelves.every((entry) => entry.hidden);
    };
    const announce = (state, name, fallback) => {
      const status = state.shelf.querySelector('[data-discovery-status]');
      if (status) status.textContent = state.shelf.dataset[name] || fallback;
    };
    const eligible = (source, state) => {
      const key = cardKey(source);
      if (!key || seen.has(key)) return false;
      // Product availability and the selected variant's own stock/minimum are
      // separate facts. A Choose options link alone cannot establish either.
      if (source.dataset.rudcaProductAvailable !== 'true' || source.dataset.rudcaVariantAvailable !== 'true') return false;
      const minimum = Number(source.dataset.rudcaVariantMin);
      if (!Number.isFinite(minimum) || minimum < 1) return false;
      if (source.hasAttribute('data-rudca-variant-max')) {
        const maximum = Number(source.dataset.rudcaVariantMax);
        if (!Number.isFinite(maximum) || maximum < minimum) return false;
      }
      if (state.shelf.dataset.discoveryVendor && source.dataset.rudcaProductVendor !== state.shelf.dataset.discoveryVendor) return false;
      if (source.querySelector('.product-item__action-button.button--disabled')) return false;
      return !!source.querySelector('form.rudca-product-form[action*="/cart/add"], a[href^="mailto:"]');
    };
    const prepareCard = (source, state) => {
      const card = source.cloneNode(true);
      card.querySelectorAll('script').forEach((script) => script.remove());
      card.classList.remove('product-item--horizontal', 'product-item--list');
      card.classList.add('product-item--vertical');
      const prefix = `discovery-${root.dataset.sectionId}-${state.shelf.dataset.discoveryShelf}-${state.nextIndex++}-`;
      const remappedIds = new Map();
      const identified = [...(card.hasAttribute('id') ? [card] : []), ...card.querySelectorAll('[id]')];
      identified.forEach((element) => {
        const originalId = element.getAttribute('id');
        remappedIds.set(originalId, prefix + originalId);
        element.setAttribute('id', prefix + originalId);
      });
      card.querySelectorAll('[for], [form], [list], [aria-describedby], [aria-labelledby], [aria-controls], [aria-owns], [aria-activedescendant]').forEach((element) => {
        for (const attribute of ['for', 'form', 'list', 'aria-describedby', 'aria-labelledby', 'aria-controls', 'aria-owns', 'aria-activedescendant']) {
          if (!element.hasAttribute(attribute)) continue;
          element.setAttribute(attribute, element.getAttribute(attribute).split(/\s+/).map((id) => remappedIds.get(id) || id).join(' '));
        }
      });
      card.querySelectorAll('[href^="#"]').forEach((element) => {
        const id = element.getAttribute('href').slice(1);
        if (remappedIds.has(id)) element.setAttribute('href', '#' + remappedIds.get(id));
      });
      card.querySelectorAll('[data-secondary-action="open-quick-view"]').forEach((element) => element.setAttribute('aria-controls', `modal-quick-view-${root.dataset.sectionId}`));
      card.querySelectorAll('.color-swatch__radio[name]').forEach((element) => { element.name = prefix + element.name; });
      const item = document.createElement('li');
      item.className = 'rudca-discovery__item';
      item.dataset.discoveryProductUrl = card.querySelector('.product-item__title').getAttribute('href');
      if (source.dataset.rudcaProductId) item.dataset.discoveryProductId = source.dataset.rudcaProductId;
      item.append(card);
      return item;
    };
    const appendBuffer = (state, amount) => {
      let added = 0;
      while (state.buffer.length && added < amount) {
        const source = state.buffer.shift();
        if (!eligible(source, state)) continue;
        const item = prepareCard(source, state);
        seen.add(cardKey(item));
        state.list.append(item);
        added++;
      }
      return added;
    };
    const loadPage = async (state) => {
      const cursor = state.cursor;
      const requestController = new AbortController();
      const abort = () => requestController.abort();
      controller.signal.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, 15000);
      let responseURL;
      let html;
      try {
        const response = await fetch(cursor.href, { credentials: 'same-origin', signal: requestController.signal });
        if (!response.ok) throw new Error('Collection unavailable');
        responseURL = response.url ? pageURL(response.url, state, cursor.href) : cursor;
        if (cursorKey(responseURL) !== cursorKey(cursor)) throw new Error('Collection cursor redirected');
        html = new DOMParser().parseFromString(await response.text(), 'text/html');
      } finally {
        clearTimeout(timeout);
        controller.signal.removeEventListener('abort', abort);
      }
      const collection = html.querySelector('[data-section-type="collection"]');
      const grid = collection?.querySelector('.product-list--collection');
      const empty = collection?.querySelector('.empty-state .empty-state__description');
      if (!collection || (!grid && !empty)) throw new Error('Incomplete collection response');
      const nextLink = collection.querySelector('.pagination a[rel="next"], .pagination__next[href]');
      const next = nextLink ? pageURL(nextLink.getAttribute('href'), state, responseURL.href) : null;
      // A repeated cursor is an unknown result, never evidence of the final page.
      if (next && (cursorKey(next) === cursorKey(cursor) || state.visited.has(cursorKey(next)))) throw new Error('Collection cursor did not advance');
      const cards = grid ? Array.from(grid.querySelectorAll('.product-item')) : [];
      if (grid && !cards.length && !empty) throw new Error('Incomplete collection grid');
      if (cards.some((card) => !cardKey(card) || !['true', 'false'].includes(card.dataset.rudcaProductAvailable) || !['true', 'false'].includes(card.dataset.rudcaVariantAvailable) || !Number.isFinite(Number(card.dataset.rudcaVariantMin)) || Number(card.dataset.rudcaVariantMin) < 1 || (card.hasAttribute('data-rudca-variant-max') && !Number.isFinite(Number(card.dataset.rudcaVariantMax))) || (state.shelf.dataset.discoveryVendor && !card.hasAttribute('data-rudca-product-vendor')))) {
        throw new Error('Incomplete product availability');
      }
      if (controller.signal.aborted || !root.isConnected) return false;
      state.buffer.push(...cards);
      state.visited.add(cursorKey(cursor));
      state.cursor = next;
      state.checked = true;
      return true;
    };
    let observeTail = () => {};
    const loadMore = async (state, explicit = false) => {
      if (state.loading || !hasMore(state) || controller.signal.aborted || !root.isConnected) return;
      if (!explicit && (state.error || state.paused)) return;
      state.loading = true;
      state.error = false;
      state.paused = false;
      announce(state, 'discoveryLoading', 'Loading more products…');
      updateShelf(state);
      let added = 0;
      let requests = 0;
      try {
        // Buffer a native page, then reveal small batches. Skip at most three
        // duplicate/unavailable-only pages per interaction, without a request loop.
        while (added < batchSize && hasMore(state)) {
          added += appendBuffer(state, batchSize - added);
          if (added || !state.cursor || requests >= 3) break;
          requests++;
          if (!await loadPage(state)) return;
        }
        state.paused = added === 0 && hasMore(state);
        if (added) {
          announce(state, 'discoveryLoaded', 'More products loaded.');
          publishProducts();
        } else {
          const status = state.shelf.querySelector('[data-discovery-status]');
          if (status) status.textContent = '';
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          state.error = true;
          announce(state, 'discoveryError', 'Couldn’t load more products.');
        }
      } finally {
        state.loading = false;
        if (!controller.signal.aborted && root.isConnected) {
          updateShelf(state);
          observeTail(state);
        }
      }
    };
    const maybeLoad = (state) => {
      if (state.visible && nearEnd(state)) loadMore(state);
    };
    for (const shelf of shelves) {
      const list = shelf.querySelector('[data-discovery-list]');
      const source = sourceURL(shelf);
      const state = { shelf, list, source, cursor: source, buffer: [], visited: new Set(), checked: false, error: false, paused: false, loading: false, visible: !window.IntersectionObserver, nextIndex: list.children.length };
      states.set(shelf, state);
      const update = () => updateShelf(state);
      const move = async (forward) => {
        const atLoadedEnd = list.scrollWidth - list.clientWidth - Math.abs(list.scrollLeft) <= 2;
        if (forward && atLoadedEnd && hasMore(state)) {
          await loadMore(state, true);
          if (controller.signal.aborted || !root.isConnected || state.error) return;
        }
        const direction = getComputedStyle(list).direction === 'rtl' ? -1 : 1;
        const amount = Math.max(1, list.clientWidth - 24) * direction * (forward ? 1 : -1);
        list.scrollBy({ left: amount, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        if (forward && nearEnd(state)) loadMore(state, true);
      };
      shelf.querySelector('[data-discovery-previous]').addEventListener('click', () => move(false), { signal: controller.signal });
      shelf.querySelector('[data-discovery-next]').addEventListener('click', () => move(true), { signal: controller.signal });
      shelf.querySelector('[data-discovery-retry]')?.addEventListener('click', () => loadMore(state, true), { signal: controller.signal });
      shelf.querySelector('[data-discovery-more]')?.addEventListener('click', () => loadMore(state, true), { signal: controller.signal });
      list.addEventListener('scroll', () => { update(); maybeLoad(state); }, { passive: true, signal: controller.signal });
      list.addEventListener('keydown', (event) => {
        if (event.target !== list) return;
        if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          const direction = getComputedStyle(list).direction === 'rtl' ? -1 : 1;
          list.scrollTo({ left: event.key === 'Home' ? 0 : list.scrollWidth * direction, behavior: 'auto' });
          if (event.key === 'End') loadMore(state, true);
        }
      }, { signal: controller.signal });
      if (window.ResizeObserver) {
        const observer = new ResizeObserver(update);
        observer.observe(list);
        cleanups.push(() => observer.disconnect());
      }
      window.addEventListener('resize', update, { passive: true, signal: controller.signal });
      update();
    }
    if (window.IntersectionObserver) {
      const viewportObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const state = states.get(entry.target);
          state.visible = entry.isIntersecting;
          if (state.visible) maybeLoad(state);
        });
      }, { rootMargin: '160px 0px' });
      for (const state of states.values()) {
        const tailObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) maybeLoad(state);
        }, { root: state.list, rootMargin: '0px 180px' });
        state.tailObserver = tailObserver;
        viewportObserver.observe(state.shelf);
        cleanups.push(() => tailObserver.disconnect());
      }
      observeTail = (state) => {
        state.tailObserver.disconnect();
        if (hasMore(state) && state.list.lastElementChild) state.tailObserver.observe(state.list.lastElementChild);
      };
      states.forEach(observeTail);
      cleanups.push(() => viewportObserver.disconnect());
    } else {
      // Older browsers keep the sparse server-rendered shelf usable; subsequent
      // batches are driven by scrolling or the shelf's own next arrow.
      states.forEach((state) => { if (state.list.children.length < 4) loadMore(state); });
    }
    publishProducts();
    instances.set(root, () => {
      controller.abort();
      cleanups.forEach((cleanup) => cleanup());
      instances.delete(root);
    });
  };
  const scan = (scope) => {
    if (scope.matches?.('[data-rudca-discovery]')) initialize(scope);
    scope.querySelectorAll('[data-rudca-discovery]').forEach(initialize);
  };
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.('[data-rudca-discovery]')) instances.get(event.target)?.();
    event.target.querySelectorAll('[data-rudca-discovery]').forEach((root) => instances.get(root)?.());
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
})();
